// Client de Originación — `GET /backoffice/origination/analytics` de `retail-wizard-business`.
//
// Sin query params: el endpoint es silo-wide y devuelve los tres chips de ventana en una respuesta.
//
// 🔴 **Por qué hay un normalizador y no un cast.** `apiFetch` devuelve `wrapper.data` SIN validar.
// Cuando el contrato del front y la respuesta del lambda dejaron de coincidir, las pantallas que
// tocaban el campo faltante con `.map()` o `.length` reventaron, y las que solo lo leían se
// pintaron en blanco sin un error en consola. Acá el borde deja el shape sano o no deja nada: los
// arreglos son arreglos, los números son números y los `null` sobreviven como `null`.
import { apiFetch } from '../../../client';
import type {
    ChannelBreakdown,
    ChannelTotals,
    FirstInstallmentCohortKpi,
    MarketCaptureKpi,
    OriginationAnalyticsResponse,
    OriginationApi,
    OriginationDailyPoint,
    OriginationFinancierCell,
    OriginationRangeBlocks,
    OriginationRiskMix,
    OriginationVolumeKpi,
    RiskLevelCell,
    SciAmountPoint,
    StoreCohortPoint,
    StoreOriginationRow,
    StoreQuadrant,
} from './originationContract';
import { STORE_QUADRANTS } from './originationContract';

const BASE_URL = import.meta.env.VITE_API_RETAIL_WIZARD_BASE_URL;

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const asRecord = (value: unknown): Record<string, unknown> => (isRecord(value) ? value : {});

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

/** Un contador del silo. Lo que no sea número finito cuenta como cero: es un conteo, no una tasa. */
const asCount = (value: unknown): number =>
    typeof value === 'number' && Number.isFinite(value) ? value : 0;

/** Una tasa del silo. Lo que no sea número finito es `null` — «no hay base», nunca un cero falso. */
const asRate = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;

const asText = (value: unknown): string | null => (typeof value === 'string' ? value : null);

const asFlag = (value: unknown): boolean => value === true;

const asTotals = (value: unknown): ChannelTotals => {
    const raw = asRecord(value);
    return { units: asCount(raw.units), cents: asCount(raw.cents) };
};

const asBreakdown = (value: unknown): ChannelBreakdown => {
    const raw = asRecord(value);
    return {
        sureKeep: asTotals(raw.sureKeep),
        otherFinanciers: asTotals(raw.otherFinanciers),
        cash: asTotals(raw.cash),
        total: asTotals(raw.total),
    };
};

/** Un punto sin `day` no se puede ubicar en el eje: se descarta en vez de pintarse en una fecha falsa. */
const asDailyPoints = (value: unknown): OriginationDailyPoint[] =>
    asArray(value).flatMap((item) => {
        const raw = asRecord(item);
        const day = asText(raw.day);
        return day === null ? [] : [{ day, ...asBreakdown(raw) }];
    });

const asVolume = (value: unknown): OriginationVolumeKpi => {
    const raw = asRecord(value);
    return {
        units: asCount(raw.units),
        cents: asCount(raw.cents),
        previousUnits: asCount(raw.previousUnits),
        unitsChangePct: asRate(raw.unitsChangePct),
    };
};

const asMarketCapture = (value: unknown): MarketCaptureKpi => {
    const raw = asRecord(value);
    return {
        capturePct: asRate(raw.capturePct),
        previousCapturePct: asRate(raw.previousCapturePct),
        changePp: asRate(raw.changePp),
        sureKeepUnits: asCount(raw.sureKeepUnits),
        totalUnits: asCount(raw.totalUnits),
    };
};

const asFinancierCells = (value: unknown): OriginationFinancierCell[] =>
    asArray(value).flatMap((item) => {
        const raw = asRecord(item);
        const financier = asText(raw.financier);
        return financier === null ? [] : [{ financier, units: asCount(raw.units) }];
    });

/** Una fila sin `storeId` no se puede identificar ni desempatar: se descarta. */
const asStoreRanking = (value: unknown): StoreOriginationRow[] =>
    asArray(value).flatMap((item) => {
        const raw = asRecord(item);
        const storeId = asText(raw.storeId);
        if (storeId === null) return [];
        return [
            {
                storeId,
                storeName: asText(raw.storeName),
                zoneName: asText(raw.zoneName),
                channels: asBreakdown(raw.channels),
                capturePct: asRate(raw.capturePct),
                captureChangePp: asRate(raw.captureChangePp),
                financierMix: asFinancierCells(raw.financierMix),
                unknownFinancierUnits: asCount(raw.unknownFinancierUnits),
                downPaymentAvgPct: asRate(raw.downPaymentAvgPct),
                minDownPaymentAvgPct: asRate(raw.minDownPaymentAvgPct),
                downPaymentOverMinPp: asRate(raw.downPaymentOverMinPp),
                firstInstallmentCohortPct: asRate(raw.firstInstallmentCohortPct),
                firstInstallmentEligibleCredits: asCount(raw.firstInstallmentEligibleCredits),
                sciAvg: asRate(raw.sciAvg),
            },
        ];
    });

/** Una barra sin `level` no se puede nombrar ni colorear: se descarta antes de llegar al cuadro. */
const asRiskLevels = (value: unknown): RiskLevelCell[] =>
    asArray(value).flatMap((item) => {
        const raw = asRecord(item);
        const level = asText(raw.level);
        if (level === null) return [];
        return [
            {
                level,
                credits: asCount(raw.credits),
                pct: asRate(raw.pct),
                downPaymentAvgPct: asRate(raw.downPaymentAvgPct),
                minDownPaymentAvgPct: asRate(raw.minDownPaymentAvgPct),
                downPaymentOverMinPp: asRate(raw.downPaymentOverMinPp),
            },
        ];
    });

/** Un punto sin SCI o sin monto no se puede ubicar en el plano: se descarta, no se pinta en cero. */
const asScatter = (value: unknown): SciAmountPoint[] =>
    asArray(value).flatMap((item) => {
        const raw = asRecord(item);
        const sci = asRate(raw.sci);
        const financedCents = asRate(raw.financedCents);
        return sci === null || financedCents === null ? [] : [{ sci, financedCents }];
    });

/** El bloque C5. Un `riskMix` ausente queda vacío: el cuadro se oculta, no se pinta en cero. */
const asRiskMix = (value: unknown): OriginationRiskMix => {
    const raw = asRecord(value);
    return {
        levels: asRiskLevels(raw.levels),
        totalLeveled: asCount(raw.totalLeveled),
        employeeCredits: asCount(raw.employeeCredits),
        unknownLevelCredits: asCount(raw.unknownLevelCredits),
        sciAvg: asRate(raw.sciAvg),
        scatter: asScatter(raw.scatter),
        scatterTruncated: asFlag(raw.scatterTruncated),
    };
};

/** El KPI de la cosecha. Un bloque ausente queda en `null` con cero elegibles: eso es N/D. */
const asCohortKpi = (value: unknown): FirstInstallmentCohortKpi => {
    const raw = asRecord(value);
    return {
        cohortPct: asRate(raw.cohortPct),
        previousCohortPct: asRate(raw.previousCohortPct),
        changePp: asRate(raw.changePp),
        eligibleCredits: asCount(raw.eligibleCredits),
        paidCredits: asCount(raw.paidCredits),
        previousEligibleCredits: asCount(raw.previousEligibleCredits),
    };
};

/** Un cuadrante que el front no conoce vale `null`: se pinta el punto y no se le inventa zona. */
const asQuadrant = (value: unknown): StoreQuadrant | null =>
    STORE_QUADRANTS.find((quadrant) => quadrant === value) ?? null;

/** Una tienda sin `storeId` no se puede casar con su fila del ranking: se descarta. */
const asStoreScatter = (value: unknown): StoreCohortPoint[] =>
    asArray(value).flatMap((item) => {
        const raw = asRecord(item);
        const storeId = asText(raw.storeId);
        if (storeId === null) return [];
        return [
            {
                storeId,
                storeName: asText(raw.storeName),
                sciAvg: asRate(raw.sciAvg),
                cohortPct: asRate(raw.cohortPct),
                sureKeepUnits: asCount(raw.sureKeepUnits),
                capturePct: asRate(raw.capturePct),
                quadrant: asQuadrant(raw.quadrant),
            },
        ];
    });

const asRangeBlocks = (value: unknown): OriginationRangeBlocks => {
    const raw = asRecord(value);
    return {
        from: asText(raw.from) ?? '',
        to: asText(raw.to) ?? '',
        daily: asDailyPoints(raw.daily),
        volume: asVolume(raw.volume),
        marketCapture: asMarketCapture(raw.marketCapture),
        storeRanking: asStoreRanking(raw.storeRanking),
        riskMix: asRiskMix(raw.riskMix),
        firstInstallmentCohort: asCohortKpi(raw.firstInstallmentCohort),
        storeScatter: asStoreScatter(raw.storeScatter),
    };
};

/**
 * Deja la respuesta con el shape del contrato pase lo que pase.
 *
 * `rosterAvailable`, `riskMixAvailable`, `plansAvailable` y `firstInstallmentCohortAvailable` se
 * leen al revés que las otras banderas:
 * solo un `true` explícito cuenta como fuente leída. Un campo ausente por un contrato viejo
 * pintaría el tablero como sano teniendo los datos incompletos, que es justo lo que evitan.
 */
export function normalizeOriginationAnalytics(raw: unknown): OriginationAnalyticsResponse {
    const body = asRecord(raw);
    const ranges = Object.fromEntries(
        Object.entries(asRecord(body.ranges)).map(([key, block]) => [key, asRangeBlocks(block)]),
    );
    return {
        ranges,
        retailersScanned: asCount(body.retailersScanned),
        rosterAvailable: asFlag(body.rosterAvailable),
        truncatedCurrent: asFlag(body.truncatedCurrent),
        truncatedPrevious: asFlag(body.truncatedPrevious),
        riskMixAvailable: asFlag(body.riskMixAvailable),
        plansAvailable: asFlag(body.plansAvailable),
        creditsTruncated: asFlag(body.creditsTruncated),
        firstInstallmentCohortAvailable: asFlag(body.firstInstallmentCohortAvailable),
        cohortPreviousTruncated: asFlag(body.cohortPreviousTruncated),
    };
}

export const originationClient: OriginationApi = {
    getOriginationAnalytics: async () => {
        const response = await apiFetch('/backoffice/origination/analytics', {
            baseUrl: BASE_URL,
        });
        return normalizeOriginationAnalytics(response);
    },
};
